import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaMagnifyingGlass, FaPenToSquare } from 'react-icons/fa6';
import { useAppContext } from '../../../../shared/context/AppContext';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { getUserId } from '../../../../shared/domain/services/userService';
// DEMO USER LOGIC COMMENTED OUT - No longer restricting access
// import { getAccessState } from '../../../demoBanner/domain/services/demoAccessService';
import * as promptLibraryService from '../../data/services/promptLibraryService';
import * as adminPromptFetchService from '../../data/services/adminPromptFetchService';
import * as categoryService from '../../data/services/categoryService';
import { getUserRoleSetup } from '../../../roleSetup/data/services/userRoleService';
import { calculateMaxResponsibilityCardHeight } from '../utils/responsibilityCardHeight';
import CategorySelector from '../components/CategorySelector';
import PromptCard from '../components/PromptCard';
import ResponsibilityCard from '../components/ResponsibilityCard';
import WhiteTextLogo from '../../../../white_text_logo.svg';
import MakerspaceLightLogo from '../../../../makerspace_light_logo.png';
import './PromptLibraryScreen.css';
import '../components/Section.css';

const PromptLibraryScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organization } = useAppContext();
  const { trackEvent, trackDebounced } = useAnalytics();
  const [activeView, setActiveView] = useState('role'); // 'role', 'favorites', 'own', 'discover'
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [favoritePrompts, setFavoritePrompts] = useState([]);
  const [ownPrompts, setOwnPrompts] = useState([]);
  const [discoverPrompts, setDiscoverPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [roleSetup, setRoleSetup] = useState(null);
  const [loadingRoleSetup, setLoadingRoleSetup] = useState(false);
  const libraryTogglesRef = useRef(null);
  const previousLocationKeyRef = useRef(null);
  const initialViewSetRef = useRef(false);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);


  useEffect(() => {
    if (user && organization) {
      loadUserSpecificData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, organization]);

  // Load role setup when activeView is 'role'
  const loadRoleSetup = useCallback(async () => {
    if (!user || activeView !== 'role') {
      return;
    }

    try {
      setLoadingRoleSetup(true);
      const orgId = organization?.id || organization?.orgId;
      const data = await getUserRoleSetup(orgId || null);
      setRoleSetup(data);
    } catch (err) {
      console.error('Error loading role setup:', err);
      setRoleSetup(null);
    } finally {
      setLoadingRoleSetup(false);
    }
  }, [user, organization, activeView]);

  useEffect(() => {
    if (activeView === 'role' && !searchQuery.trim()) {
      loadRoleSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, searchQuery, user, organization]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  // Set default view based on user role
  useEffect(() => {
    // Reset flag when user logs out
    if (!user) {
      initialViewSetRef.current = false;
      return;
    }

    // Skip if already set initial view
    if (initialViewSetRef.current) {
      return;
    }

    // Set default view to role if user has completed role setup
    const roleCompleted = user.roleCompleted === true || user.roleCompleted === 1 || user.roleCompleted === '1';
    if (roleCompleted && user.jobRole) {
      setActiveView('role');
    } else {
      // Fallback to 'own' if role not set
      setActiveView('own');
    }
    
    initialViewSetRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);



  // Refresh data when navigating back from add-prompt screen
  useEffect(() => {
    // Check if location key changed (which happens even with replace: true)
    if (previousLocationKeyRef.current !== null && previousLocationKeyRef.current !== location.key && location.pathname === '/home' && organization) {
      // Refresh all data when returning to the library screen
      loadInitialData();
      if (user) {
        loadUserSpecificData();
      }
    }
    previousLocationKeyRef.current = location.key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.pathname, organization]);


  // Helper function to deduplicate prompts by ID
  const deduplicatePrompts = (prompts) => {
    const seen = new Set();
    return prompts.filter(prompt => {
      if (seen.has(prompt.id)) {
        return false;
      }
      seen.add(prompt.id);
      return true;
    });
  };

  // Helper function to extract categories from prompts and merge with existing categories
  const updateCategoriesFromPrompts = (prompts, existingCategories = []) => {
    const categorySet = new Set(existingCategories);
    
    prompts.forEach(prompt => {
      // Extract from categories array
      if (prompt.categories && Array.isArray(prompt.categories)) {
        prompt.categories.forEach(cat => {
          if (cat && typeof cat === 'string') {
            categorySet.add(cat);
          } else if (cat && typeof cat === 'object' && cat.categoryTitle) {
            categorySet.add(cat.categoryTitle);
          }
        });
      }
      // Also check single category field for backward compatibility
      if (prompt.category && typeof prompt.category === 'string') {
        categorySet.add(prompt.category);
      }
    });
    
    return Array.from(categorySet).sort();
  };

  const loadInitialData = async () => {
    try {
      const orgId = organization?.id || organization?.orgId;
      
      // Load categories
      const categoriesData = await categoryService.getAllCategories({ orgId: orgId || null, isGlobal: true });
      setCategories(categoriesData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setLoading(false);
    }
  };

  const loadUserSpecificData = async () => {
    try {
      const userId = user?.id || user?.userId;
      const orgId = organization?.id || organization?.orgId;
      if (!userId) {
        return;
      }

      const isAdmin = user?.role && user.role.toLowerCase() === 'admin';

      if (isAdmin) {
        const [favorites, own] = await Promise.all([
          adminPromptFetchService.getAdminFavoritePrompts(userId),
          adminPromptFetchService.getAdminUserPrompts(userId),
        ]);
        const favoritesAsPlainObjects = favorites.map(p => p.toJSON ? p.toJSON() : {
          ...p,
          categories: p.categories || [],
          category: p.category || '',
        });
        const ownAsPlainObjects = own.map(p => p.toJSON ? p.toJSON() : {
          ...p,
          categories: p.categories || [],
          category: p.category || '',
        });
        const deduplicatedFavorites = deduplicatePrompts(favoritesAsPlainObjects);
        const deduplicatedOwn = deduplicatePrompts(ownAsPlainObjects);
        setFavoritePrompts(deduplicatedFavorites);
        setOwnPrompts(deduplicatedOwn);
        setCategories(prevCategories => {
          const updated = updateCategoriesFromPrompts([...deduplicatedFavorites, ...deduplicatedOwn], prevCategories);
          return updated;
        });
        const discoverList = await adminPromptFetchService.getAdminDiscoverPrompts(userId, deduplicatedFavorites, deduplicatedOwn);
        const deduplicatedDiscover = deduplicatePrompts(discoverList);
        setDiscoverPrompts(deduplicatedDiscover);
        setCategories(prevCategories => {
          const updated = updateCategoriesFromPrompts(deduplicatedDiscover, prevCategories);
          return updated;
        });
        return;
      }

      const [favorites, own] = await Promise.all([
        promptLibraryService.getFavoritePrompts(userId, orgId),
        promptLibraryService.getUserPrompts(userId, orgId || null),
      ]);
      
      // Convert Prompt instances to plain objects to ensure all properties are preserved in React state
      const favoritesAsPlainObjects = favorites.map(p => p.toJSON ? p.toJSON() : {
        ...p,
        categories: p.categories || [],
        category: p.category || '',
      });
      const ownAsPlainObjects = own.map(p => p.toJSON ? p.toJSON() : {
        ...p,
        categories: p.categories || [],
        category: p.category || '',
      });
      
      // Deduplicate prompts by ID
      const deduplicatedFavorites = deduplicatePrompts(favoritesAsPlainObjects);
      const deduplicatedOwn = deduplicatePrompts(ownAsPlainObjects);
      
      setFavoritePrompts(deduplicatedFavorites);
      setOwnPrompts(deduplicatedOwn);
      
      // Update categories to include categories from favorites and own prompts
      setCategories(prevCategories => {
        const updated = updateCategoriesFromPrompts([...deduplicatedFavorites, ...deduplicatedOwn], prevCategories);
        return updated;
      });
      
      // Load discover prompts after favorites and own prompts are loaded
      loadDiscoverPrompts(userId, orgId, deduplicatedFavorites, deduplicatedOwn);
    } catch (error) {
      console.error('[PromptLibraryScreen] Error loading user data:', error);
    }
  };

  const loadDiscoverPrompts = async (userId, orgId, favoritePromptsList = [], ownPromptsList = []) => {
    try {
      if (!userId) {
        return;
      }
      const isAdmin = user?.role && user.role.toLowerCase() === 'admin';
      if (isAdmin) {
        const discoverList = await adminPromptFetchService.getAdminDiscoverPrompts(userId, favoritePromptsList, ownPromptsList);
        const deduplicatedDiscover = deduplicatePrompts(discoverList);
        setDiscoverPrompts(deduplicatedDiscover);
        setCategories(prevCategories => {
          const updated = updateCategoriesFromPrompts(deduplicatedDiscover, prevCategories);
          return updated;
        });
        return;
      }
      if (!orgId) {
        return;
      }
      const allPromptsData = await promptLibraryService.getAllPrompts({
        organizationId: orgId,
        userId: userId,
        promptType: 'all',
      });
      const allPromptsAsPlainObjects = allPromptsData.map(p => p.toJSON ? p.toJSON() : {
        ...p,
        categories: p.categories || [],
        category: p.category || '',
      });
      const favoriteIds = new Set(favoritePromptsList.map(p => p.id));
      const ownIds = new Set(ownPromptsList.map(p => p.id));
      const filteredPrompts = allPromptsAsPlainObjects.filter(prompt => {
        return !favoriteIds.has(prompt.id) && !ownIds.has(prompt.id);
      });
      const deduplicatedDiscover = deduplicatePrompts(filteredPrompts);
      setDiscoverPrompts(deduplicatedDiscover);
      setCategories(prevCategories => {
        const updated = updateCategoriesFromPrompts(deduplicatedDiscover, prevCategories);
        return updated;
      });
    } catch (error) {
      console.error('[PromptLibraryScreen] Error loading discover prompts:', error);
    }
  };

  const handleCategorySelect = (categories) => {
    setSelectedCategories(categories);
    // Track category filter event
    trackEvent(EVENTS.CATEGORY_FILTERED, {
      categories: categories,
      selectedCount: categories.length,
    });
  };

  const handleDeletePrompt = (promptId) => {
    // Remove prompt from ownPrompts state immediately
    setOwnPrompts(prev => prev.filter(prompt => prompt.id !== promptId));
  };

  // Update prompt in all arrays where it exists
  const handlePromptUpdate = (updatedPrompt, preserveFavoriteState = true) => {
    const promptId = updatedPrompt.id;
    
    // Helper to merge prompt update while preserving favorite state and other fields
    const mergePromptUpdate = (existingPrompt) => {
      // Start with existing prompt to preserve all fields
      const mergedPrompt = { ...existingPrompt, ...updatedPrompt };
      
      if (preserveFavoriteState && existingPrompt && existingPrompt.isFavorite !== undefined) {
        // Preserve favorite state from existing prompt to prevent vote updates from clearing it
        // Only update if explicitly set to true (favoriting)
        if (updatedPrompt.isFavorite === true) {
          // Explicitly favoriting - allow it
          mergedPrompt.isFavorite = true;
        } else {
          // Preserve existing favorite state (vote update with false/undefined shouldn't clear true)
          mergedPrompt.isFavorite = existingPrompt.isFavorite;
        }
      } else if (updatedPrompt.isFavorite !== undefined) {
        // Not preserving favorite state (from favorite toggle) or no existing state - use updated value
        mergedPrompt.isFavorite = updatedPrompt.isFavorite;
      }
      
      // Ensure category is preserved if not in updated prompt
      if (!mergedPrompt.category && existingPrompt?.category) {
        mergedPrompt.category = existingPrompt.category;
      }
      
      // Ensure categories array is preserved if not in updated prompt
      if ((!mergedPrompt.categories || !Array.isArray(mergedPrompt.categories) || mergedPrompt.categories.length === 0) 
          && existingPrompt?.categories && Array.isArray(existingPrompt.categories) && existingPrompt.categories.length > 0) {
        mergedPrompt.categories = existingPrompt.categories;
      }
      
      return mergedPrompt;
    };
    
    // Update in favoritePrompts
    setFavoritePrompts(prev => {
      const exists = prev.some(p => p.id === promptId);
      if (exists) {
        return prev.map(p => p.id === promptId ? mergePromptUpdate(p) : p);
      }
      return prev;
    });
    
    // Update in ownPrompts
    setOwnPrompts(prev => {
      const exists = prev.some(p => p.id === promptId);
      if (exists) {
        return prev.map(p => p.id === promptId ? mergePromptUpdate(p) : p);
      }
      return prev;
    });
    
    // Update in discoverPrompts
    setDiscoverPrompts(prev => {
      const exists = prev.some(p => p.id === promptId);
      if (exists) {
        return prev.map(p => p.id === promptId ? mergePromptUpdate(p) : p);
      }
      return prev;
    });
  };

  // Handle favorite toggle - updates prompt and manages favorites array
  const handleFavoriteToggle = (updatedPrompt, isFavorited) => {
    const promptId = updatedPrompt.id;
    
    // Convert Prompt instance to plain object to ensure all properties are preserved
    // This is important because Prompt instances might not serialize correctly in React state
    const promptPlainObject = updatedPrompt.toJSON ? updatedPrompt.toJSON() : {
      ...updatedPrompt,
      // Ensure categories is explicitly included
      categories: updatedPrompt.categories || [],
      category: updatedPrompt.category || '',
    };
    
    // Ensure the prompt has the correct favorite state before updating
    const promptWithFavoriteState = {
      ...promptPlainObject,
      isFavorite: isFavorited,
    };
    
    // Update prompts without preserving favorite state (we want to set it explicitly)
    handlePromptUpdate(promptWithFavoriteState, false);
    
    // Then manage favorites array
    if (isFavorited) {
      // Add to favorites if not already present, or update existing
      setFavoritePrompts(prev => {
        const exists = prev.some(p => p.id === promptId);
        if (!exists) {
          return [...prev, promptWithFavoriteState];
        }
        // Update existing favorite - preserve categories from existing if not in updated
        return prev.map(p => {
          if (p.id === promptId) {
            const merged = { ...p, ...promptWithFavoriteState };
            // Preserve categories from existing if not in updated
            if ((!merged.categories || !Array.isArray(merged.categories) || merged.categories.length === 0) 
                && p.categories && Array.isArray(p.categories) && p.categories.length > 0) {
              merged.categories = p.categories;
            }
            return merged;
          }
          return p;
        });
      });
      
      // Remove from discover prompts if it exists there (since discover excludes favorites)
      setDiscoverPrompts(prev => prev.filter(p => p.id !== promptId));
    } else {
      // Remove from favorites
      setFavoritePrompts(prev => prev.filter(p => p.id !== promptId));
      
      // If removing from favorites, also remove from discover prompts if it exists there
      // This shouldn't happen since discover excludes favorites, but handle for consistency
      setDiscoverPrompts(prev => prev.filter(p => p.id !== promptId));
    }
  };

  // Get prompts based on active view
  const getPromptsForView = () => {
    let prompts = [];
    switch (activeView) {
      case 'favorites':
        prompts = favoritePrompts;
        break;
      case 'own':
        prompts = ownPrompts;
        break;
      case 'discover':
        prompts = discoverPrompts;
        break;
      case 'role':
        // Role view handles its own prompts via RoleView component
        prompts = [];
        break;
      default:
        prompts = [];
    }
    return prompts;
  };

  // Perform global search via API
  const performSearch = async (query) => {
    if (!query || !query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const orgId = organization?.id || organization?.orgId;
      const userId = getUserId(user);
      
      const filters = {
        organizationId: orgId || null,
        userId: userId || null,
        promptType: 'all', // Search both org and global prompts
        search: query.trim(),
      };
      
      const results = await promptLibraryService.getAllPrompts(filters);
      
      // Sort by priority: Own prompts first, then Title (1) → Category (2) → Text (3)
      const queryLower = query.toLowerCase().trim();
      results.sort((a, b) => {
        // First priority: Own prompts appear first
        const aIsOwn = userId && a.creatorId === userId;
        const bIsOwn = userId && b.creatorId === userId;
        if (aIsOwn && !bIsOwn) return -1;
        if (!aIsOwn && bIsOwn) return 1;
        
        // Then sort by search relevance
        const getPriority = (prompt) => {
          // Priority 1: Title match
          const title = (prompt.title || '').toLowerCase();
          if (title.includes(queryLower)) return 1;
          
          // Priority 2: Category match
          let promptCategories = [];
          if (prompt.categories && Array.isArray(prompt.categories) && prompt.categories.length > 0) {
            // Categories can be strings or objects with categoryTitle
            promptCategories = prompt.categories.map(cat => {
              if (typeof cat === 'string') return cat;
              if (cat && typeof cat === 'object' && cat.categoryTitle) return cat.categoryTitle;
              return '';
            }).filter(Boolean);
          } else if (prompt.category) {
            promptCategories = [prompt.category];
          }
          if (promptCategories.some(cat => (cat || '').toLowerCase().includes(queryLower))) {
            return 2;
          }
          
          // Priority 3: Description or promptText match
          const description = (prompt.description || '').toLowerCase();
          const promptText = (prompt.promptText || '').toLowerCase();
          if (description.includes(queryLower) || promptText.includes(queryLower)) {
            return 3;
          }
          
          return 999; // Should not happen if filter worked correctly
        };
        return getPriority(a) - getPriority(b);
      });
      
      setSearchResults(results);
      
      // Track search event (debounced)
      trackDebounced(EVENTS.SEARCH_PERFORMED, {
        query: query.trim(),
        resultCount: results.length,
        selectedCategories: selectedCategories.length > 0 ? selectedCategories : undefined,
      }, 500);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Filter prompts by selected categories (for non-search views)
  const getFilteredPrompts = () => {
    let prompts = getPromptsForView();
    
    // Apply category filter
    if (selectedCategories.length > 0) {
      prompts = prompts.filter((prompt) => {
        // Get all categories for the prompt (handle both array and single category)
        let promptCategories = [];
        if (prompt.categories && Array.isArray(prompt.categories) && prompt.categories.length > 0) {
          promptCategories = prompt.categories;
        } else if (prompt.category) {
          promptCategories = [prompt.category];
        }
        
        // Check if any of the prompt's categories match any selected category
        return promptCategories.some(cat => selectedCategories.includes(cat));
      });
    }
    
    // Sort prompts so own prompts appear first
    const userId = getUserId(user);
    if (userId) {
      prompts = prompts.sort((a, b) => {
        const aIsOwn = a.creatorId === userId;
        const bIsOwn = b.creatorId === userId;
        if (aIsOwn && !bIsOwn) return -1;
        if (!aIsOwn && bIsOwn) return 1;
        return 0; // Keep original order for prompts with same ownership status
      });
    }
    
    return prompts;
  };

  // Use search results if searching, otherwise use filtered prompts from current view
  const displayedPrompts = searchQuery.trim() ? searchResults : getFilteredPrompts();

  // DEMO USER LOGIC COMMENTED OUT - No longer restricting access
  // Get access state for demo access restrictions
  // const accessState = getAccessState(user);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="prompt-library-screen">
      <header className="library-header">
        <div className="library-logos">
          <img src={WhiteTextLogo} alt="Framework" className="library-logo" />
          <span className="library-logo-divider">|</span>
          <img src={MakerspaceLightLogo} alt="MakerSpace" className="library-logo" />
        </div>
        <h1 className="library-title">
          Defence Prompt Library
        </h1>
      </header>

      <div className="library-toggles" ref={libraryTogglesRef}>
        <div className={`search-input-container ${isSearchExpanded ? 'expanded' : ''}`} data-tutorial-target="search-input">
          <FaMagnifyingGlass className="search-icon" />
          <input
            type="text"
            className="search-input-field"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              const newValue = e.target.value;
              const wasEmpty = !searchQuery.trim();
              const nowHasContent = newValue.trim().length > 0;
              
              setSearchQuery(newValue);
              
              // Expand when content is added (handles typing, paste, etc.)
              if (wasEmpty && nowHasContent && !isSearchExpanded) {
                setIsSearchExpanded(true);
              }
              
              // Collapse if search becomes empty
              if (!newValue.trim()) {
                setIsSearchExpanded(false);
                setSearchResults([]);
                setIsSearching(false);
                // Clear any pending debounce timer
                if (searchDebounceTimer) {
                  clearTimeout(searchDebounceTimer);
                  setSearchDebounceTimer(null);
                }
              } else {
                // Clear existing timer
                if (searchDebounceTimer) {
                  clearTimeout(searchDebounceTimer);
                }
                
                // Set new debounce timer (500ms delay)
                const timer = setTimeout(() => {
                  performSearch(newValue);
                }, 500);
                setSearchDebounceTimer(timer);
              }
            }}
            onFocus={() => {
              // Expand when user focuses on search
              if (searchQuery.trim()) {
                setIsSearchExpanded(true);
              }
            }}
            onKeyDown={(e) => {
              // Expand on first character typed
              if (!isSearchExpanded && e.key !== 'Escape' && e.key !== 'Enter' && e.key.length === 1) {
                setIsSearchExpanded(true);
              }
              // Collapse on Escape key
              if (e.key === 'Escape') {
                setIsSearchExpanded(false);
                setSearchQuery('');
                setSearchResults([]);
                setIsSearching(false);
                if (searchDebounceTimer) {
                  clearTimeout(searchDebounceTimer);
                  setSearchDebounceTimer(null);
                }
                e.target.blur();
              }
            }}
            onBlur={() => {
              // Collapse when clicking outside
              setIsSearchExpanded(false);
            }}
            aria-label="Search"
          />
        </div>
        {!isSearchExpanded && (
          <>
            {/* DEMO USER LOGIC COMMENTED OUT - No longer showing conditional "Your prompts" button */}
            {/* {user && (
              <>
                Show "Your prompts" first when demo is expired
                {accessState.shouldGreyOutPrompts && (
                  <button
                    className={`toggle-button ${activeView === 'own' ? 'active' : ''}`}
                    onClick={() => setActiveView('own')}
                  >
                    Your prompts
                  </button>
                )}
              </>
            )} */}
            {user && (
              <>
                <button
                  className={`toggle-button ${activeView === 'role' ? 'active' : ''}`}
                  onClick={() => setActiveView('role')}
                >
                  {user.jobRole === 'Product Manager' ? 'PM Prompts' : user.jobRole && user.jobRole !== 'Other' ? `${user.jobRole} Prompts` : 'Job Prompts'}
                </button>
                <button
                  className={`toggle-button ${activeView === 'favorites' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveView('favorites');
                    // Reload favorites when switching to favorites view
                    if (user && organization) {
                      loadUserSpecificData();
                    }
                  }}
                >
                  Your favourites
                </button>
                <button
                  className={`toggle-button ${activeView === 'own' ? 'active' : ''}`}
                  onClick={() => setActiveView('own')}
                >
                  Your prompts
                </button>
                {/* DEMO USER LOGIC COMMENTED OUT - No longer greying out Discover button */}
                {/* className={`toggle-button ${accessState.shouldGreyOutPrompts ? 'greyed-out' : ''} ${activeView === 'discover' ? 'active' : ''}`} */}
                <button
                  className={`toggle-button ${activeView === 'discover' ? 'active' : ''}`}
                  data-tutorial-target="discover-tab"
                  onClick={() => {
                    setActiveView('discover');
                    // Reload discover prompts when switching to discover view
                    if (user && organization) {
                      const userId = getUserId(user);
                      const orgId = organization?.id || organization?.orgId;
                      loadDiscoverPrompts(userId, orgId, favoritePrompts, ownPrompts);
                    }
                  }}
                >
                  Discover
                </button>
              </>
            )}
            <div className="category-selector-desktop">
              <CategorySelector
                categories={categories}
                selectedCategories={selectedCategories}
                onSelectCategories={handleCategorySelect}
              />
            </div>
          </>
        )}
      </div>

      <div className="prompts-grid-container">
        {activeView === 'role' && !searchQuery.trim() ? (
          <div className="responsibility-cards-container">
            {loadingRoleSetup ? (
              <div className="empty-message">Loading your role-based prompts...</div>
            ) : roleSetup && roleSetup.responsibilities && roleSetup.responsibilities.length > 0 ? (
              <>
                <button
                  className="role-view-edit-button"
                  onClick={() => navigate('/edit-role')}
                  aria-label="Edit responsibilities and frameworks"
                >
                  <FaPenToSquare /> Edit role
                </button>
                {(() => {
                  // Sort responsibilities
                  const sortedResponsibilities = [...roleSetup.responsibilities].sort((a, b) => {
                    // Sort by displayOrder if available
                    const aOrder = a.displayOrder !== undefined && a.displayOrder !== null ? a.displayOrder : null;
                    const bOrder = b.displayOrder !== undefined && b.displayOrder !== null ? b.displayOrder : null;
                    
                    if (aOrder !== null && bOrder !== null) {
                      return aOrder - bOrder;
                    }
                    
                    if (aOrder !== null && bOrder === null) return -1;
                    if (aOrder === null && bOrder !== null) return 1;
                    
                    // Fallback to original custom sort logic
                    if (a.responsibilityId === 'professional-development') return -1;
                    if (b.responsibilityId === 'professional-development') return 1;
                    
                    if (!a.isUniversal && b.isUniversal) return -1;
                    if (a.isUniversal && !b.isUniversal) return 1;
                    
                    return a.responsibilityName.localeCompare(b.responsibilityName);
                  });

                  // Calculate fixed height based on maximum prompts across all responsibilities
                  const fixedHeight = calculateMaxResponsibilityCardHeight(
                    sortedResponsibilities,
                    roleSetup.frameworks
                  );

                  return (
                    <div 
                      className="responsibility-cards-grid"
                      style={{ '--responsibility-card-height': `${fixedHeight}px` }}
                    >
                      {sortedResponsibilities.map((responsibility, index) => {
                        const frameworks = roleSetup.frameworks[responsibility.responsibilityId] || [];
                        return (
                          <ResponsibilityCard
                            key={responsibility.responsibilityId}
                            responsibility={responsibility}
                            prompts={frameworks}
                            colorIndex={index}
                          />
                        );
                      })}
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="empty-message">
                <p>No responsibilities configured for your role.</p>
                <button
                  className="role-view-setup-button"
                  onClick={() => navigate('/edit-role')}
                >
                  Select a role or add own responsibilities
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="cards-grid">
            {isSearching ? (
              <div className="empty-message">Searching...</div>
            ) : displayedPrompts.length > 0 ? (
              displayedPrompts.map((prompt) => {
                // Calculate if prompt is owned by current user by comparing creatorId with user ID
                const userId = getUserId(user);
                const isOwnPrompt = userId && prompt.creatorId === userId;
                
                return (
                  // DEMO USER LOGIC COMMENTED OUT - No longer greying out prompts
                  // shouldGreyOutPrompts={accessState.shouldGreyOutPrompts}
                  <PromptCard
                    key={prompt.id}
                    prompt={prompt}
                    isOwnPrompt={isOwnPrompt}
                    onDelete={isOwnPrompt ? handleDeletePrompt : undefined}
                    onUpdate={handlePromptUpdate}
                    onFavoriteToggle={handleFavoriteToggle}
                    selectedCategories={selectedCategories}
                  />
                );
              })
            ) : (
              <div className="empty-message">
                {isSearching ? (
                  'Searching...'
                ) : searchQuery.trim() ? (
                  'No prompts found matching your search'
                ) : (
                  <>
                    {activeView === 'favorites' && 'No favorite prompts yet'}
                    {activeView === 'own' && "You haven't created any prompts yet"}
                    {activeView === 'discover' && 'No prompts to discover'}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptLibraryScreen;

